"use client"

import { motion } from "framer-motion"
import { BookOpen, Brain, Zap, Heart, Eye, Ear, MessageCircle, Target } from "lucide-react"
import { useState } from "react"

export default function LearnView() {
  const [selectedTopic, setSelectedTopic] = useState(null)

  const topics = [
    {
      id: "brain-basics",
      title: "Brain Basics",
      icon: Brain,
      color: "#667eea",
      description: "Learn about brain structure and function",
      lessons: [
        "Introduction to the Brain",
        "Major Brain Regions",
        "Neurons and Synapses",
        "Brain Development"
      ]
    },
    {
      id: "cognitive-functions",
      title: "Cognitive Functions",
      icon: Zap,
      color: "#4cc9f0",
      description: "Understand how the brain processes information",
      lessons: [
        "Memory Formation",
        "Attention and Focus",
        "Decision Making",
        "Problem Solving"
      ]
    },
    {
      id: "emotions",
      title: "Emotions & Mood",
      icon: Heart,
      color: "#ff006e",
      description: "Explore the neuroscience of emotions",
      lessons: [
        "Emotional Processing",
        "Mood Regulation",
        "Stress Response",
        "Emotional Intelligence"
      ]
    },
    {
      id: "senses",
      title: "Sensory Processing",
      icon: Eye,
      color: "#06ffa5",
      description: "How the brain interprets sensory information",
      lessons: [
        "Visual Processing",
        "Auditory System",
        "Touch and Pain",
        "Taste and Smell"
      ]
    },
    {
      id: "language",
      title: "Language & Communication",
      icon: MessageCircle,
      color: "#ffd60a",
      description: "The neural basis of language",
      lessons: [
        "Language Centers",
        "Speech Production",
        "Reading and Writing",
        "Bilingualism"
      ]
    },
    {
      id: "motor",
      title: "Movement & Coordination",
      icon: Target,
      color: "#7209b7",
      description: "How the brain controls movement",
      lessons: [
        "Motor Cortex",
        "Cerebellum Function",
        "Motor Learning",
        "Balance and Posture"
      ]
    }
  ]

  const brainFacts = [
    "The human brain contains approximately 86 billion neurons",
    "Your brain uses about 20% of your body's total energy",
    "The brain can process information as fast as 268 mph",
    "Neuroplasticity allows your brain to reorganize throughout life",
    "The brain generates about 12-25 watts of electricity",
    "Information can travel through neurons at speeds up to 268 mph"
  ]

  return (
    <div className="learn-view">
      <motion.div
        className="learn-header"
        initial={{ opacity: 0, y: -20 }}
        animate={{ opacity: 1, y: 0 }}
      >
        <h1 className="learn-title">
          <BookOpen className="title-icon" />
          Learn About the Brain
        </h1>
        <p className="learn-subtitle">
          Discover the fascinating world of neuroscience
        </p>
      </motion.div>

      {/* Topics Grid */}
      <div className="topics-grid">
        {topics.map((topic, index) => {
          const Icon = topic.icon
          return (
            <motion.div
              key={topic.id}
              className="topic-card glass-card"
              initial={{ opacity: 0, scale: 0.9 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ delay: index * 0.1 }}
              whileHover={{ scale: 1.05, y: -5 }}
              onClick={() => setSelectedTopic(topic)}
            >
              <div className="topic-icon" style={{ backgroundColor: `${topic.color}20`, color: topic.color }}>
                <Icon size={32} />
              </div>
              
              <div className="topic-content">
                <h3 className="topic-title">{topic.title}</h3>
                <p className="topic-description">{topic.description}</p>
                
                <div className="topic-lessons">
                  <span className="lessons-count">{topic.lessons.length} lessons</span>
                </div>
              </div>

              <motion.button
                className="topic-action-btn"
                whileHover={{ scale: 1.1 }}
                whileTap={{ scale: 0.9 }}
                style={{ backgroundColor: topic.color }}
              >
                Start Learning
              </motion.button>
            </motion.div>
          )
        })}
      </div>

      {/* Brain Facts Section */}
      <motion.div
        className="brain-facts-section"
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.5 }}
      >
        <h2 className="section-title">
          <Brain size={24} />
          Did You Know?
        </h2>
        <div className="facts-grid">
          {brainFacts.map((fact, index) => (
            <motion.div
              key={index}
              className="fact-card glass-card"
              initial={{ opacity: 0, x: -20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ delay: 0.6 + index * 0.1 }}
              whileHover={{ scale: 1.03 }}
            >
              <div className="fact-number">{index + 1}</div>
              <p className="fact-text">{fact}</p>
            </motion.div>
          ))}
        </div>
      </motion.div>

      {/* Interactive Brain Map */}
      <motion.div
        className="interactive-section glass-card"
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.8 }}
      >
        <h2 className="section-title">
          <Target size={24} />
          Interactive Brain Map
        </h2>
        <p className="section-description">
          Click on different brain regions to learn about their functions (Coming Soon)
        </p>
        <div className="coming-soon-placeholder">
          <Brain size={64} color="#667eea" />
          <p>Interactive 3D brain map in development</p>
        </div>
      </motion.div>

      {/* Lesson Detail Modal */}
      {selectedTopic && (
        <motion.div
          className="lesson-modal-overlay"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          onClick={() => setSelectedTopic(null)}
        >
          <motion.div
            className="lesson-modal glass-card"
            initial={{ scale: 0.9, y: 50 }}
            animate={{ scale: 1, y: 0 }}
            onClick={(e) => e.stopPropagation()}
          >
            <div className="modal-header">
              <h2>{selectedTopic.title}</h2>
              <button className="close-btn" onClick={() => setSelectedTopic(null)}>×</button>
            </div>
            <div className="modal-content">
              <p>{selectedTopic.description}</p>
              <h3>Lessons:</h3>
              <ul className="lessons-list">
                {selectedTopic.lessons.map((lesson, index) => (
                  <li key={index}>
                    <span className="lesson-number">{index + 1}</span>
                    {lesson}
                  </li>
                ))}
              </ul>
              <button className="start-course-btn" style={{ backgroundColor: selectedTopic.color }}>
                Start Course
              </button>
            </div>
          </motion.div>
        </motion.div>
      )}
    </div>
  )
}
